import { describeIssues, fieldPath } from '../validation/form-errors';
import { ApiErrorCode, type ApiFieldError } from './api-types';

/**
 * Erreur unique levée par le client HTTP.
 *
 * Tout ce que l'interface a besoin de savoir pour réagir est ici : le `status`
 * pour la logique technique, le `code` pour le message affiché, `details` pour
 * repositionner les erreurs sous les champs d'un formulaire.
 */
export class ApiError extends Error {
  readonly summaryMessage: string;
  readonly status: number;
  readonly code: string;
  readonly details: ApiFieldError[];
  readonly requestId: string | undefined;

  constructor(params: {
    message: string;
    status: number;
    code: string;
    details?: ApiFieldError[];
    requestId?: string;
  }) {
    super(
      params.details?.length && params.status < 500
        ? `${params.message}\n${describeIssues(params.details)}`
        : params.message,
    );
    this.summaryMessage = params.message;
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.details = params.details ?? [];
    this.requestId = params.requestId;
  }

  /** `true` si la session est à renouveler ou l'utilisateur à reconnecter. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /** `true` si réessayer a du sens (panne passagère, quota, réseau). */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  /**
   * Erreurs de validation indexées par nom de champ.
   * Le backend préfixe le champ par sa section (`body.email`) : le préfixe est
   * retiré pour correspondre au nom utilisé dans les formulaires.
   */
  get fieldErrors(): Record<string, string> {
    return this.details.reduce<Record<string, string>>((accumulator, detail) => {
      const field = fieldPath(detail.field);
      accumulator[field] ??= detail.message;
      return accumulator;
    }, {});
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/** Erreur réseau : le serveur n'a pas répondu (hors ligne, DNS, CORS, timeout). */
export const networkError = (cause: unknown): ApiError => {
  const error = new ApiError({
    message: 'Impossible de joindre le serveur. Vérifiez votre connexion.',
    status: 0,
    code: ApiErrorCode.NETWORK_ERROR,
  });
  // La cause d'origine reste attachée pour le débogage, sans être affichée.
  error.cause = cause;
  return error;
};
